import {
  CheckOutlined,
  CloseOutlined,
  ExclamationCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';
import { Button } from '@components/Button/Button';
import { Modal } from '@components/common/Modal/Modal';
import { App, Popover, Spin, Tooltip } from 'antd';
import { useEffect, useRef, useState } from 'react';

import { useGetDictionariesQuery } from '@/api/dictionaries/api';
import { useUpdateTaskIndividualMutation } from '@/api/vaccination/task/api';
import {
  addDescription,
  addDisease,
} from '@/pages/TaskTablet/components/farmIndividual/addDisease';
import { getExecutorGeolocation } from '@/pages/TaskTablet/components/getExecutorGeolocation';
import { ProtocolIndividual } from '@/pages/Vaccination/components/task/types';
import { DOSAGE_UNIT_LABELS_SHORT } from '@/shared/pharmacy/enum';
import {
  CANCEL_REASON,
  cancelReasonMap,
  getGeolocationUndefinedText,
  individualVaccinationStatus,
  VACCINATION_STATUS,
} from '@/shared/vaccination/enums';

import cl from './styles.module.scss';

interface TabletFarmIndividualProps extends ProtocolIndividual {
  isDisabled?: boolean;
  isEventApproved: boolean;
  isDuplicate?: boolean;
  onInventoryClick?: (value: string) => void;
}

export const TabletFarmIndividual = ({
  id,
  status,
  orderNumber,
  inventoryNumber,
  location,
  dosage,
  dosageUnit,
  cancelReason,
  cancelDescription,
  isVaccinated,
  isDisabled,
  isNearDivision,
  geolocationError,
  isEventApproved,
  diseaseDescription,
  individualDiseaseDescription,
  individualLocationId,
  isDuplicate,
  onInventoryClick,
}: TabletFarmIndividualProps) => {
  const { message } = App.useApp();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState<boolean>(false);
  const [openPopover, setOpenPopover] = useState(false);
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [translateX, setTranslateX] = useState(0);

  const { data: dicts } = useGetDictionariesQuery();

  const [triggerUpdateIndividual, { isLoading: indLoading }] =
    useUpdateTaskIndividualMutation();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        !isModalOpen &&
        !isCancelModalOpen &&
        openPopover &&
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node)
      ) {
        setOpenPopover(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openPopover, isModalOpen, isCancelModalOpen]);

  const warnGeolocationUndefined = (text: string) => {
    message.warning(text);
  };

  const handleReason = async (
    selectedReason: CANCEL_REASON,
    cancelDescription?: string,
    diseaseDescription?: string,
  ) => {
    setOpenPopover(false);
    if (isEventApproved || isDisabled) return;

    try {
      setIsGettingLocation(true);
      const geolocation = await getExecutorGeolocation(warnGeolocationUndefined);

      await triggerUpdateIndividual({
        isVaccinated: false,
        id,
        ...geolocation,
        cancelReason: selectedReason,
        cancelDescription,
        diseaseDescription,
      }).unwrap();
    } catch (e: any) {
      message.error(e.data?.message || 'Ошибка при сохранении');
    } finally {
      setIsGettingLocation(false);
    }
  };

  const handleClear = async () => {
    if (isEventApproved || isVaccinated === null) return;

    try {
      setIsGettingLocation(true);
      const geolocation = await getExecutorGeolocation(warnGeolocationUndefined);

      await triggerUpdateIndividual({
        isVaccinated: null,
        id,
        ...geolocation,
        cancelReason: null,
      }).unwrap();
    } catch (e: any) {
      message.error(e.data?.message || 'Ошибка при сохранении');
    } finally {
      setIsGettingLocation(false);
    }
  };

  const getIcon = (isVaccinated: boolean | null) => {
    if (isVaccinated === true)
      return <CheckOutlined style={{ color: '#52c41a', fontSize: 20 }} />;
    if (isVaccinated === false) {
      return <CloseOutlined style={{ color: '#ff4d4f', fontSize: 20 }} />;
    }
    return null;
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (isEventApproved || isDisabled) return;

    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now(),
    };
    setTranslateX(0);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStartRef.current || isEventApproved || isDisabled) return;

    const touch = e.touches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;

    const maxShift = 25;
    const shift = Math.max(-maxShift, Math.min(maxShift, deltaX));

    setTranslateX(shift);
  };

  const handleTouchEnd = async (e: React.TouchEvent) => {
    if (isEventApproved || isDisabled || !touchStartRef.current) return;

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    const deltaTime = Date.now() - touchStartRef.current.time;

    if (Math.abs(deltaY) < 50 && Math.abs(deltaX) > 50 && deltaTime < 2000) {
      if (deltaX < 0) {
        setTimeout(() => setOpenPopover(true), 150);
      } else if (!isVaccinated) {
        setOpenPopover(false);
        try {
          setIsGettingLocation(true);
          const geolocation = await getExecutorGeolocation(warnGeolocationUndefined);

          await triggerUpdateIndividual({
            isVaccinated: true,
            id,
            ...geolocation,
            cancelReason: null,
          }).unwrap();
        } catch (e: any) {
          message.error(e.data?.message || 'Ошибка при сохранении');
        } finally {
          setIsGettingLocation(false);
        }
      }
    }

    setTranslateX(0);
    touchStartRef.current = null;
  };

  return (
    <>
      <Modal
        mode={'form'}
        schema={addDisease()}
        title={'Заболевание особи'}
        data={{ diseaseDescription: individualDiseaseDescription }}
        open={isModalOpen}
        width="370px"
        onSubmit={async ({ diseaseDescription }) => {
          await handleReason(CANCEL_REASON.SICK, undefined, diseaseDescription);
          setIsModalOpen(false);
        }}
        onCancel={() => {
          setIsModalOpen(false);
        }}
        layout={{ formLayout: 'vertical', itemLayout: 'vertical' }}
        formKey={'addForm'}
        destroyOnHidden={true}
      />
      <Modal
        mode={'form'}
        schema={addDescription()}
        title={'Дополнительные сведения'}
        data={{ cancelDescription: cancelDescription }}
        open={isCancelModalOpen}
        width="370px"
        onSubmit={async ({ cancelDescription }) => {
          await handleReason(CANCEL_REASON.LACK_OF_VACCINE, cancelDescription);
          setIsCancelModalOpen(false);
        }}
        onCancel={() => {
          setIsCancelModalOpen(false);
        }}
        layout={{ formLayout: 'vertical', itemLayout: 'vertical' }}
        formKey={'addForm'}
        destroyOnHidden={true}
      />
      <div
        className={`${cl.wrap} ${indLoading || isGettingLocation ? cl.loading : ''} ${isDuplicate ? cl.duplicate : ''}`}
        style={{
          background: status !== VACCINATION_STATUS.RESERVE ? '#CCCCCC' : 'unset',
          transform: `translateX(${translateX}px)`,
          transition: 'transform 0.2s ease',
        }}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}>
        {isNearDivision === false && (
          <ExclamationCircleOutlined className={cl.icon} style={{ color: '#E31F1F' }} />
        )}
        {isNearDivision == null && geolocationError && (
          <Tooltip title={getGeolocationUndefinedText(geolocationError)}>
            <QuestionCircleOutlined className={cl.icon} style={{ color: '#8C8C8C' }} />
          </Tooltip>
        )}
        <span style={{ textAlign: 'center' }}>
          {status !== VACCINATION_STATUS.RESERVE ? orderNumber : ''}
        </span>
        <span className={cl.status}>
          {individualVaccinationStatus[status]}{' '}
          {individualDiseaseDescription && (
            <Popover
              classNames={{
                root: cl.popoverDisease,
              }}
              content={individualDiseaseDescription}
              placement="bottom"
              destroyOnHidden>
              <Button
                type="text"
                className={cl.disease}
                icon={<ExclamationCircleOutlined style={{ color: '#046094' }} />}
              />
            </Popover>
          )}
        </span>
        <span
          onClick={(e) => {
            e.stopPropagation();
            onInventoryClick?.(inventoryNumber);
          }}
          style={{ cursor: 'pointer' }}>
          {inventoryNumber}
        </span>
        <span>
          {location ??
            dicts?.dictMap.INDIVIDUAL_LOCATION[individualLocationId]?.name ??
            ''}
        </span>
        <span style={{ textAlign: 'center' }}>
          {dosage === null
            ? ''
            : `${Number(dosage.toFixed(3))} ${DOSAGE_UNIT_LABELS_SHORT[dosageUnit] ?? ''}`}
        </span>
        <Popover
          classNames={{
            root: cl.popoverReasonWrap,
          }}
          trigger={[]}
          content={
            <div
              ref={popoverRef}
              className={cl.popoverReason}
              onClick={(e) => e.stopPropagation()}>
              <Button
                text={cancelReasonMap[CANCEL_REASON.SICK]}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsModalOpen(true);
                }}
              />
              <Button
                text={cancelReasonMap[CANCEL_REASON.DEAD]}
                onClick={(e) => {
                  e.stopPropagation();
                  handleReason(CANCEL_REASON.DEAD);
                }}
              />
              <Button
                text={cancelReasonMap[CANCEL_REASON.LACK_OF_VACCINE]}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsCancelModalOpen(true);
                }}
              />
            </div>
          }
          open={openPopover && !isEventApproved && !isDisabled}
          destroyOnHidden={true}>
          <div
            className={cl.customCheckbox}
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}>
            {getIcon(isVaccinated)}
          </div>
        </Popover>
        <span className={cl.reason}>
          {cancelDescription ?? (cancelReason ? cancelReasonMap[cancelReason] : '')}
          {diseaseDescription && (
            <Popover
              classNames={{
                root: cl.popoverDisease,
              }}
              content={diseaseDescription}
              placement="bottom"
              destroyOnHidden>
              <Button
                type="text"
                className={cl.disease}
                icon={<ExclamationCircleOutlined style={{ color: '#046094' }} />}
              />
            </Popover>
          )}
        </span>
        {(indLoading || isGettingLocation) && <Spin className={cl.spin} />}
      </div>
    </>
  );
};
